/**
 * Utility functions for wage formatting and calculations
 */

/**
 * Format wage display with appropriate suffix based on wage type
 * @param {number|string} wage - The wage amount
 * @param {string} wageType - The wage type (hourly, weekly, monthly, yearly)
 * @returns {string} Formatted wage string
 */
export const formatWageDisplay = (wage, wageType = 'hourly') => {
  if (!wage || wage === '' || wage == null) {
    return 'Not Set';
  }
  
  const numericWage = typeof wage === 'string' ? parseFloat(wage) : wage;
  if (isNaN(numericWage)) {
    return 'Invalid';
  }
  
  const formattedAmount = numericWage.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: wageType === 'hourly' ? 2 : 0,
    maximumFractionDigits: wageType === 'hourly' ? 2 : 0
  });
  
  const suffixes = {
    hourly: '/ hr',
    weekly: '/ week',
    monthly: '/ month',
    yearly: '/ year'
  };
  
  return `${formattedAmount} ${suffixes[wageType] || '/ hr'}`;
};

/**
 * Convert wage between different time periods
 * @param {number} wage - The wage amount
 * @param {string} fromType - Current wage type
 * @param {string} toType - Target wage type
 * @param {number} hoursPerWeek - Working hours per week (default: 40)
 * @returns {number} Converted wage amount
 */
export const convertWage = (wage, fromType, toType, hoursPerWeek = 40) => {
  if (!wage || fromType === toType) return wage;
  
  const numericWage = typeof wage === 'string' ? parseFloat(wage) : wage;
  if (isNaN(numericWage)) return 0;
  
  // Convert everything to hourly first
  let hourlyWage;
  switch (fromType) {
    case 'hourly':
      hourlyWage = numericWage;
      break;
    case 'weekly':
      hourlyWage = numericWage / hoursPerWeek;
      break;
    case 'monthly':
      hourlyWage = numericWage / (hoursPerWeek * 4.33); // Average weeks per month
      break;
    case 'yearly':
      hourlyWage = numericWage / (hoursPerWeek * 52);
      break;
    default:
      return numericWage;
  }
  
  // Convert from hourly to target type
  switch (toType) {
    case 'hourly':
      return Math.round(hourlyWage * 100) / 100;
    case 'weekly':
      return Math.round(hourlyWage * hoursPerWeek);
    case 'monthly':
      return Math.round(hourlyWage * hoursPerWeek * 4.33);
    case 'yearly':
      return Math.round(hourlyWage * hoursPerWeek * 52);
    default:
      return Math.round(hourlyWage * 100) / 100;
  }
};

/**
 * Get wage type options for dropdowns
 * @returns {Array} Array of wage type options
 */
export const getWageTypeOptions = () => [
  { value: 'hourly', label: 'Hourly', shortLabel: 'hr' },
  { value: 'weekly', label: 'Weekly', shortLabel: 'week' },
  { value: 'monthly', label: 'Monthly', shortLabel: 'month' },
  { value: 'yearly', label: 'Yearly', shortLabel: 'year' }
];

/**
 * Validate wage type
 * @param {string} wageType - The wage type to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidWageType = (wageType) => {
  const validTypes = ['hourly', 'weekly', 'monthly', 'yearly'];
  return validTypes.includes(wageType);
};

/**
 * Get wage type label
 * @param {string} wageType - The wage type
 * @param {boolean} short - Whether to return short label
 * @returns {string} The wage type label
 */
export const getWageTypeLabel = (wageType, short = false) => {
  const options = getWageTypeOptions();
  const option = options.find(opt => opt.value === wageType);
  return option ? (short ? option.shortLabel : option.label) : 'Unknown';
};

/**
 * Calculate annual salary equivalent for comparison purposes
 * @param {number} wage - The wage amount
 * @param {string} wageType - The wage type
 * @param {number} hoursPerWeek - Working hours per week (default: 40)
 * @returns {number} Annual equivalent salary
 */
export const calculateAnnualEquivalent = (wage, wageType, hoursPerWeek = 40) => {
  return convertWage(wage, wageType, 'yearly', hoursPerWeek);
};
